import { Box, Chip, type ChipProps } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { getCaseStatusColor } from 'data/constants/caseStatus';
import { getClientStatusLabelKey } from 'helpers/applicationRetainers';
import useTranslate from 'hooks/useTranslate';
import { toRgba } from 'utils/toRgba';

interface CaseStatusChipProps extends Omit<ChipProps, 'label' | 'color' | 'icon'> {
    status: string;
    /** Replaces the translated status name, e.g. to prefix a share of the price. */
    label?: ReactNode;
}

const CaseStatusChip = forwardRef<HTMLDivElement, CaseStatusChipProps>(
    ({ status, label, size = 'small', sx, ...chipProps }, ref) => {
        const { translate } = useTranslate();
        const color = getCaseStatusColor(status);
        const labelKey = getClientStatusLabelKey(status);

        return (
            <Chip
                {...chipProps}
                ref={ref}
                size={size}
                icon={
                    <Box
                        component="span"
                        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor' }}
                    />
                }
                label={label ?? (labelKey ? translate(labelKey) : status)}
                sx={[
                    {
                        color,
                        bgcolor: toRgba(color, 0.1),
                        fontWeight: 600,
                        '& .MuiChip-icon': { color, ml: 1, mr: -0.25 },
                        '& .MuiChip-deleteIcon': {
                            color: toRgba(color, 0.55),
                            '&:hover': { color },
                        },
                    },
                    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                ]}
            />
        );
    }
);

CaseStatusChip.displayName = 'CaseStatusChip';

export default CaseStatusChip;
